import { useState } from "react";
import { Button } from "./button";
import { useWalletContext, useAuth } from "../../context";
import { Wallet, LogOut, Shield, Loader2 } from "lucide-react";

interface WalletButtonProps {
  showAdminBadge?: boolean;
}

export function WalletButton({ showAdminBadge = true }: WalletButtonProps) {
  const { connected, connecting, publicKey, connect, disconnect } =
    useWalletContext();
  const { isAuthenticated, isAdmin, isLoading, error, login, logout } =
    useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handleConnect = async () => {
    if (!connected) {
      await connect();
    }
  };

  const handleDisconnect = async () => {
    logout();
    await disconnect();
  };

  const handleLogin = async () => {
    setIsAuthenticating(true);
    try {
      await login();
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Truncate wallet address for display
  const truncatedAddress = publicKey
    ? `${publicKey.slice(0, 4)}...${publicKey.slice(-4)}`
    : null;

  if (connecting || isLoading) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Connecting...
      </Button>
    );
  }

  if (!connected) {
    return (
      <Button variant="outline" size="sm" onClick={handleConnect}>
        <Wallet className="w-4 h-4 mr-2" />
        Connect Wallet
      </Button>
    );
  }

  if (connected && !isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handleLogin}
          disabled={isAuthenticating}
        >
          {isAuthenticating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing...
            </>
          ) : (
            <>
              <Shield className="w-4 h-4 mr-2" />
              Sign In
            </>
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={handleDisconnect}>
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {showAdminBadge && isAdmin && (
        <span className="px-2 py-1 text-xs font-medium bg-amber-500/20 text-amber-400 rounded">
          Admin
        </span>
      )}
      <span className="text-sm text-muted-foreground font-mono">
        {truncatedAddress}
      </span>
      <Button variant="ghost" size="sm" onClick={handleDisconnect}>
        <LogOut className="w-4 h-4" />
      </Button>
    </div>
  );
}
